import React, { useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import WhiteSquareButton from '../../components/Button/WhiteSquareButton';
import Modal from '../../components/modal/Modal';
import { useTickets, useCreateTicket, useUpdateTicket, useDeleteTicket } from '@/features/groupTicket/hooks';
import { Ticket, CreateTicketBody, ExpiryPolicy, ClassType } from '@/features/groupTicket/types';

const EXPIRY_POLICY_OPTIONS: { value: ExpiryPolicy; label: string }[] = [
    { value: 'FIRST', label: '둘 중 빠른 것' },
    { value: 'COUNT', label: '회차 소진 시' },
    { value: 'PERIOD', label: '기간 만료 시' },
];

const CLASS_TYPE_OPTIONS = [
    { value: 'GROUP', label: '그룹' },
    { value: 'PRIVATE_1ON2', label: '1:2 프라이빗' },
    { value: 'PRIVATE_1ON3', label: '1:3 프라이빗' },
];

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const getEffectivePerSessionFee = (ticket: {
    perSessionFee?: number;
    expiryPolicy?: ExpiryPolicy;
    price: number;
    totalCount?: number;
}) => {
    if (ticket.perSessionFee != null) return ticket.perSessionFee;
    if (ticket.expiryPolicy === 'PERIOD') return undefined;
    if (!ticket.totalCount || ticket.totalCount <= 0) return undefined;
    return Math.round(ticket.price / ticket.totalCount);
};

const GroupTicketPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const { data: tickets = [], isLoading } = useTickets({ centerId });
    const createMutation = useCreateTicket();
    const updateMutation = useUpdateTicket();
    const deleteMutation = useDeleteTicket();

    const [showCreate, setShowCreate] = useState(false);
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_groupTicket') !== 'hidden');
    const [editTarget, setEditTarget] = useState<Ticket | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Ticket | null>(null);
    const [classTypeFilter, setClassTypeFilter] = useState<'ALL' | ClassType>('ALL');

    return (
        <Col style={{ width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' }}>
            <Col style={{ width: '100%', maxWidth: 1000, margin: '0 auto', padding: '40px 32px 80px' }}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Col>
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <Subtitle1Bold>그룹 수강권 상품</Subtitle1Bold>
                            {!showGuide && (
                                <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_groupTicket'); }}
                                    style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #93C5FD', backgroundColor: '#EFF6FF', color: '#3B82F6', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            그룹수업에 연결할 수강권 상품을 등록하고 관리합니다
                        </CaptionLight>
                    </Col>
                    <ColorSquareButton text="+ 수강권 등록" size="small" callBack={() => setShowCreate(true)} />
                </Row>

                {showGuide && (
                    <Col style={{ marginTop: 16, padding: '18px 22px', backgroundColor: '#EFF6FF', borderRadius: 12, border: '1px solid #BFDBFE', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_groupTicket', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#93C5FD', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#1E40AF', marginBottom: 6 }}>그룹 수강권 상품이란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#1E40AF', lineHeight: 1.7 }}>
                            <span><b>그룹수업/프라이빗(1:2, 1:3) 수업</b>에서 사용할 수강권 상품을 등록하는 페이지입니다.</span>
                            <span>GX 체험권도 이곳에서 등록하면 회원이 <b>앱에서 그룹수업을 예약</b>할 수 있습니다.</span>
                            <span>1:1 PT 수강권은 상단 메뉴의 <b>'1:1 PT 수강권'</b>에서 별도로 관리됩니다.</span>
                        </Col>
                        <Row style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>수강권 등록</span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>→</span>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>수업에 연결</span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>→</span>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#DBEAFE', color: '#1E40AF' }}>회원에게 발급</span>
                        </Row>
                    </Col>
                )}

                {/* classType 필터 (W07) */}
                <Row style={{ marginTop: 24, gap: 6 }}>
                    {[{ value: 'ALL', label: '전체' }, ...CLASS_TYPE_OPTIONS].map(({ value, label }) => {
                        const sel = classTypeFilter === value;
                        return (
                            <button key={value} onClick={() => setClassTypeFilter(value as 'ALL' | ClassType)}
                                style={{
                                    padding: '6px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                                    border: sel ? '1.5px solid #3B82F6' : '1px solid #E1E4E6',
                                    backgroundColor: sel ? '#EFF6FF' : 'white',
                                    color: sel ? '#1E40AF' : '#6B7280', fontWeight: sel ? 600 : 400,
                                }}>{label}</button>
                        );
                    })}
                </Row>

                {/* 상품 목록 */}
                <Col style={{ marginTop: 12, gap: 12 }}>
                    {isLoading ? (
                        <span style={{ fontSize: 14, color: '#9CA3AF', textAlign: 'center', padding: '40px 0' }}>불러오는 중...</span>
                    ) : (tickets as Ticket[]).filter(t =>
                        classTypeFilter === 'ALL' || t.classType === classTypeFilter || (!t.classType && t.allowedClassTypes?.includes(classTypeFilter))
                    ).length === 0 ? (
                        <Col style={{ alignItems: 'center', padding: '60px 0', gap: 16 }}>
                            <span style={{ fontSize: 40 }}>🎫</span>
                            <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>등록된 수강권 상품이 없습니다</span>
                            <span style={{ fontSize: 14, color: '#9CA3AF' }}>수강권 상품을 등록하면 수업에 연결할 수 있습니다</span>
                        </Col>
                    ) : (
                        <div style={{ border: '1px solid #E5E7EB', borderRadius: 12, overflow: 'hidden', backgroundColor: 'white' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#F9FAFB' }}>
                                        <th style={thStyle}>상품명</th>
                                        <th style={thStyle}>타입</th>
                                        <th style={thStyle}>횟수</th>
                                        <th style={thStyle}>유효기간</th>
                                        <th style={thStyle}>판매 금액</th>
                                        <th style={thStyle}>회당 가격</th>
                                        <th style={thStyle}>수업 유형</th>
                                        <th style={thStyle}>상태</th>
                                        <th style={{ ...thStyle, width: 100 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(tickets as Ticket[]).filter(t =>
                                        classTypeFilter === 'ALL' || t.classType === classTypeFilter || (!t.classType && t.allowedClassTypes?.includes(classTypeFilter))
                                    ).map((t) => (
                                        <tr key={t._id} style={{ borderTop: '1px solid #F3F4F6' }}>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>{t.name}</td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                                    backgroundColor: t.type === 'COUNT' ? '#EFF6FF' : '#F5F3FF',
                                                    color: t.type === 'COUNT' ? '#1E40AF' : '#5B21B6',
                                                }}>{t.type === 'COUNT' ? '횟수제' : '기간제'}</span>
                                            </td>
                                            <td style={tdStyle}>{t.totalCount}회</td>
                                            <td style={tdStyle}>{t.periodDays ? `${t.periodDays}일` : '-'}</td>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>{t.price.toLocaleString()}원</td>
                                            <td style={tdStyle}>
                                                {getEffectivePerSessionFee(t) != null
                                                    ? `${getEffectivePerSessionFee(t)?.toLocaleString()}원`
                                                    : '-'}
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 3, backgroundColor: '#F3F4F6', color: '#6B7280' }}>
                                                    {t.classType === 'GROUP' ? '그룹' : t.classType === 'PRIVATE_1ON2' ? '1:2' : t.classType === 'PRIVATE_1ON3' ? '1:3' : (t.allowedClassTypes || []).map(ct => ct === 'GROUP' ? '그룹' : ct === 'PRIVATE_1ON2' ? '1:2' : '1:3').join(', ')}
                                                </span>
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{
                                                    fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                                                    backgroundColor: t.isActive ? '#ECFDF5' : '#FEF2F2',
                                                    color: t.isActive ? '#065F46' : '#991B1B',
                                                }}>{t.isActive ? '활성' : '비활성'}</span>
                                            </td>
                                            <td style={tdStyle}>
                                                <Row style={{ gap: 6 }}>
                                                    <button onClick={() => setEditTarget(t)} style={linkBtnStyle}>수정</button>
                                                    <button onClick={() => setDeleteTarget(t)} style={{ ...linkBtnStyle, color: '#EF4444' }}>삭제</button>
                                                </Row>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Col>
            </Col>

            {/* 생성/수정 모달 */}
            {(showCreate || editTarget) && (
                <TicketFormModal
                    ticket={editTarget}
                    onClose={() => { setShowCreate(false); setEditTarget(null); }}
                    createAlert={createAlert}
                    onSave={async (body) => {
                        try {
                            if (editTarget) {
                                await updateMutation.mutateAsync({ centerId, ticketId: editTarget._id, body });
                                createAlert('수정 완료', '수강권 상품이 수정되었습니다.', false);
                            } else {
                                await createMutation.mutateAsync({ centerId, body: body as CreateTicketBody });
                                createAlert('등록 완료', '수강권 상품이 등록되었습니다.', false);
                            }
                            setShowCreate(false);
                            setEditTarget(null);
                        } catch (err: any) {
                            createAlert('저장 실패', err?.serverMessage || '수강권 저장에 실패했습니다.', true);
                        }
                    }}
                    isLoading={createMutation.isLoading || updateMutation.isLoading}
                />
            )}

            {/* 삭제 확인 */}
            {deleteTarget && (
                <Modal setIsVisible={() => setDeleteTarget(null)}>
                    <Col style={{ padding: 32, alignItems: 'center', gap: 16 }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>수강권 비활성화</span>
                        <span style={{ fontSize: 14, color: '#6B7280', textAlign: 'center' }}>
                            <b>{deleteTarget.name}</b>을 비활성화하시겠습니까?<br />
                            이미 발급된 수강권에는 영향이 없습니다.
                        </span>
                        <Row style={{ gap: 8 }}>
                            <WhiteSquareButton text="취소" size="small" callBack={() => setDeleteTarget(null)} />
                            <ColorSquareButton text="비활성화" size="small"
                                callBack={async () => {
                                    try {
                                        await deleteMutation.mutateAsync({ centerId, ticketId: deleteTarget._id });
                                        createAlert('비활성화 완료', '수강권 상품이 비활성화되었습니다.', false);
                                        setDeleteTarget(null);
                                    } catch { createAlert('실패', '비활성화에 실패했습니다.', true); }
                                }}
                                isDisabled={deleteMutation.isLoading} />
                        </Row>
                    </Col>
                </Modal>
            )}
        </Col>
    );
};

/* ═══════ 수강권 생성/수정 모달 ═══════ */

const TicketFormModal = ({
    ticket, onClose, onSave, isLoading, createAlert,
}: {
    ticket: Ticket | null; onClose: () => void; onSave: (body: Partial<CreateTicketBody>) => void; isLoading: boolean; createAlert?: any;
}) => {
    const [name, setName] = useState(ticket?.name || '');
    const [expiryPolicy, setExpiryPolicy] = useState<ExpiryPolicy>(ticket?.expiryPolicy || 'FIRST');
    const [totalCount, setTotalCount] = useState<number | undefined>(ticket?.totalCount ?? 10);
    const [periodDays, setPeriodDays] = useState<number | undefined>(ticket?.periodDays ?? 90);
    const [price, setPrice] = useState(ticket?.price || 0);
    // 편집 시 기존 classType 또는 allowedClassTypes[0]에서 복원
    const [classType, setClassType] = useState<ClassType>(
        ticket?.classType || (ticket?.allowedClassTypes?.[0] as ClassType) || 'GROUP'
    );
    const [perSessionFee, setPerSessionFee] = useState<number | undefined>(ticket?.perSessionFee);
    const [noshowFee, setNoshowFee] = useState<number | undefined>(ticket?.noshowFee);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [weeklyLimit, setWeeklyLimit] = useState(ticket?.weeklyLimit || 0);
    const [dailyBookingLimit, setDailyBookingLimit] = useState(ticket?.dailyBookingLimit || 0);
    const [allowedWeekdays, setAllowedWeekdays] = useState<number[]>(ticket?.allowedWeekdays || []);
    const [bookableStartTime, setBookableStartTime] = useState(ticket?.bookableStartTime || '');
    const [bookableEndTime, setBookableEndTime] = useState(ticket?.bookableEndTime || '');
    const [cancellableCount, setCancellableCount] = useState(ticket?.cancellableCount || 0);
    const [sameDayCancelCount, setSameDayCancelCount] = useState(ticket?.sameDayCancelCount || 0);

    const toggleWeekday = (day: number) => {
        setAllowedWeekdays((prev) =>
            prev.includes(day) ? prev.filter((x) => x !== day) : [...prev, day]
        );
    };

    // perSessionFee 자동 계산 표시 (COUNT/FIRST + totalCount > 0)
    const autoPerSessionFee = expiryPolicy !== 'PERIOD' && price && totalCount && totalCount > 0
        ? Math.round(price / totalCount)
        : undefined;

    const handleSubmit = () => {
        if (!name.trim()) return;
        if (expiryPolicy === 'PERIOD' && !perSessionFee) {
            createAlert?.('입력 필요', '기간제 수강권은 회당 가격을 직접 입력해주세요.', true);
            return;
        }
        const body: Partial<CreateTicketBody> = {
            name: name.trim(),
            expiryPolicy,
            totalCount: totalCount || undefined,
            periodDays: periodDays || undefined,
            price,
            classType,
            allowedClassTypes: [classType],
            perSessionFee: perSessionFee != null ? perSessionFee : autoPerSessionFee,
            noshowFee: noshowFee != null ? noshowFee : undefined,
            weeklyLimit: weeklyLimit || undefined,
            dailyBookingLimit: dailyBookingLimit || undefined,
            allowedWeekdays: allowedWeekdays.length > 0 ? allowedWeekdays : undefined,
            bookableStartTime: bookableStartTime || undefined,
            bookableEndTime: bookableEndTime || undefined,
            cancellableCount: cancellableCount || undefined,
            sameDayCancelCount: sameDayCancelCount || undefined,
        };
        onSave(body);
    };

    return (
        <Modal isCloseButton setIsVisible={onClose} whiteViewWidth={520}>
            <Col style={{ padding: 32, overflow: 'auto', maxHeight: '85vh', gap: 16 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#111827' }}>
                    {ticket ? '수강권 수정' : '수강권 등록'}
                </span>

                {/* 기본 정보 */}
                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>상품명</Body2Regular>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: GX 10회권" style={inputStyle} />
                </Col>

                <Row style={{ gap: 12 }}>
                    <Col style={{ flex: 1, gap: 4 }}>
                        <Body2Regular color={colors.LAVEL_2}>총 횟수 (선택)</Body2Regular>
                        <input type="number" value={totalCount ?? ''} onChange={(e) => setTotalCount(e.target.value ? Number(e.target.value) : undefined)} placeholder="비우면 무제한" min={1} style={inputStyle} />
                    </Col>
                    <Col style={{ flex: 1, gap: 4 }}>
                        <Body2Regular color={colors.LAVEL_2}>유효 기간 (일, 선택)</Body2Regular>
                        <input type="number" value={periodDays ?? ''} onChange={(e) => setPeriodDays(e.target.value ? Number(e.target.value) : undefined)} placeholder="비우면 무기한" min={1} style={inputStyle} />
                    </Col>
                    <Col style={{ flex: 1, gap: 4 }}>
                        <Body2Regular color={colors.LAVEL_2}>판매 금액 (원)</Body2Regular>
                        <input inputMode="numeric" value={price ? price.toLocaleString() : ''} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setPrice(v ? Number(v) : 0); }} placeholder="예: 300,000" style={inputStyle} />
                    </Col>
                </Row>

                <Col style={{ gap: 4 }}>
                    <Body2Regular color={colors.LAVEL_2}>만료 정책</Body2Regular>
                    <Row style={{ gap: 6 }}>
                        {EXPIRY_POLICY_OPTIONS.map((opt) => {
                            const sel = expiryPolicy === opt.value;
                            return (
                                <button key={opt.value} onClick={() => setExpiryPolicy(opt.value)}
                                    style={{
                                        flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13,
                                        border: sel ? '1.5px solid #6366F1' : '1px solid #E1E4E6',
                                        backgroundColor: sel ? '#EEF2FF' : 'white',
                                        color: sel ? '#4338CA' : '#6B7280',
                                        fontWeight: sel ? 600 : 400, cursor: 'pointer',
                                    }}>
                                    {opt.label}
                                </button>
                            );
                        })}
                    </Row>
                </Col>

                {/* 수업 유형 (단일 선택) */}
                <Col style={{ gap: 6 }}>
                    <Body2Regular color={colors.LAVEL_2}>수업 유형</Body2Regular>
                    <Row style={{ gap: 6 }}>
                        {CLASS_TYPE_OPTIONS.map(({ value, label }) => {
                            const sel = classType === value;
                            return (
                                <button key={value} onClick={() => setClassType(value as ClassType)}
                                    style={{
                                        padding: '6px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                        border: sel ? '1.5px solid #10B981' : '1px solid #E1E4E6',
                                        backgroundColor: sel ? '#ECFDF5' : 'white',
                                        color: sel ? '#065F46' : '#6B7280', fontWeight: sel ? 600 : 400,
                                    }}>{label}</button>
                            );
                        })}
                    </Row>
                </Col>

                {/* 회당 가격 (정산 단가) */}
                <Col style={{ gap: 6 }}>
                    <Row style={{ gap: 12 }}>
                        <Col style={{ flex: 1, gap: 4 }}>
                            <Body2Regular color={colors.LAVEL_2}>
                                회당 가격{expiryPolicy === 'PERIOD' && <span style={{ color: '#EF4444', marginLeft: 4 }}>*필수</span>}
                            </Body2Regular>
                            <input
                                inputMode="numeric"
                                value={perSessionFee ? perSessionFee.toLocaleString() : ''}
                                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setPerSessionFee(v ? Number(v) : undefined); }}
                                placeholder={autoPerSessionFee ? `자동: ${autoPerSessionFee.toLocaleString()}원` : '직접 입력'}
                                style={inputStyle}
                            />
                            {autoPerSessionFee && !perSessionFee && (
                                <span style={{ fontSize: 11, color: '#9CA3AF' }}>미입력 시 자동 계산: {autoPerSessionFee.toLocaleString()}원 (판매금액 / 총 횟수)</span>
                            )}
                        </Col>
                        <Col style={{ flex: 1, gap: 4 }}>
                            <Body2Regular color={colors.LAVEL_2}>노쇼 회당 가격 <span style={{ fontSize: 12, color: '#9CA3AF' }}>(선택)</span></Body2Regular>
                            <input
                                inputMode="numeric"
                                value={noshowFee ? noshowFee.toLocaleString() : ''}
                                onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); setNoshowFee(v ? Number(v) : undefined); }}
                                placeholder="미설정 시 회당 가격과 동일"
                                style={inputStyle}
                            />
                        </Col>
                    </Row>
                </Col>

                {/* 이용 제한 (접기/펼치기) */}
                <button onClick={() => setShowAdvanced(!showAdvanced)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151' }}>
                    <span style={{ transform: showAdvanced ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: 12 }}>▶</span>
                    이용 제한 {showAdvanced ? '접기' : '설정'}
                </button>

                {showAdvanced && (
                    <Col style={{ padding: 16, backgroundColor: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB', gap: 12 }}>
                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>주간 최대</span>
                                <input type="number" value={weeklyLimit} onChange={(e) => setWeeklyLimit(Number(e.target.value))} min={0} placeholder="0=무제한" style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>하루 예약</span>
                                <input type="number" value={dailyBookingLimit} onChange={(e) => setDailyBookingLimit(Number(e.target.value))} min={0} placeholder="0=무제한" style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                        </Row>

                        <Col style={{ gap: 4 }}>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>가능 요일</span>
                            <Row style={{ gap: 4 }}>
                                {DAY_LABELS.map((label, idx) => {
                                    const sel = allowedWeekdays.includes(idx);
                                    return (
                                        <button key={idx} onClick={() => toggleWeekday(idx)}
                                            style={{
                                                width: 36, height: 36, borderRadius: 8, fontSize: 13, cursor: 'pointer',
                                                border: sel ? '2px solid #3B82F6' : '1px solid #E1E4E6',
                                                backgroundColor: sel ? '#EFF6FF' : 'white',
                                                color: sel ? '#2563EB' : '#6B7280', fontWeight: sel ? 600 : 400,
                                            }}>{label}</button>
                                    );
                                })}
                            </Row>
                        </Col>

                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>이용 시작 시간</span>
                                <input type="time" value={bookableStartTime} onChange={(e) => setBookableStartTime(e.target.value)} style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>이용 종료 시간</span>
                                <input type="time" value={bookableEndTime} onChange={(e) => setBookableEndTime(e.target.value)} style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                        </Row>

                        <Row style={{ gap: 12 }}>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>총 취소 횟수</span>
                                <input type="number" value={cancellableCount} onChange={(e) => setCancellableCount(Number(e.target.value))} min={0} placeholder="0=무제한" style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                            <Col style={{ flex: 1, gap: 4 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>당일 취소 횟수</span>
                                <input type="number" value={sameDayCancelCount} onChange={(e) => setSameDayCancelCount(Number(e.target.value))} min={0} placeholder="0=무제한" style={{ ...inputStyle, fontSize: 13 }} />
                            </Col>
                        </Row>
                    </Col>
                )}

                <Row style={{ gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                    <WhiteSquareButton text="취소" size="small" callBack={onClose} />
                    <ColorSquareButton text={ticket ? '수정' : '등록'} size="small" callBack={handleSubmit} isDisabled={isLoading || !name.trim()} />
                </Row>
            </Col>
        </Modal>
    );
};

const thStyle: React.CSSProperties = { padding: '10px 16px', textAlign: 'left', fontWeight: 500, color: '#6B7280', fontSize: 13 };
const tdStyle: React.CSSProperties = { padding: '10px 16px', color: '#374151', fontSize: 14 };
const linkBtnStyle: React.CSSProperties = { fontSize: 12, color: '#3B82F6', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' };

export default GroupTicketPage;
